/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { FormStore } from "@formisch/react"
import { Form, Field as FormField, getInput, useForm } from "@formisch/react"
import { CircleAlertIcon, LockIcon } from "lucide-react"
import { useNavigate, useSearchParams } from "react-router"
import * as v from "valibot"

import IapLogo from "../../assets/images/logo-iap.svg?react"
import { ResetPasswordSchema } from "../../features/session/password-schema"
import { SessionViewModel } from "../../features/session/session-view-model"
import { LoggerViewModel } from "../../logger/logger-viewmodel"
import { AppRoutes } from "../../routes/routes"
import { isFieldInvalid } from "../../utils/is-field-invalid"
import { useViewModel } from "../app/app-hooks"
import { BusyButton } from "../components/busy-button"
import { PasswordInput } from "../components/password-input"
import { Alert, AlertDescription, AlertTitle } from "../primitives/alert"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "../primitives/card"
import { Field, FieldError, FieldGroup, FieldLabel } from "../primitives/field"

import styles from "./password-reset-view.module.css"

/**
 * Page opened by a password reset link.
 *
 * The link carries the reset token in the `token` query parameter. Without a
 * token, show an invalid-link message instead of a form that can never submit
 * successfully. After a successful reset, continue to login.
 */
export const PasswordResetView = () => {
    const [searchParams] = useSearchParams()
    const token = searchParams.get("token")

    return (
        <div className={styles.root}>
            <div className={styles.container}>
                <Card className={styles.card}>
                    <CardHeader>
                        <CardTitle>
                            <h1 className="m-0 text-xl font-bold tracking-wide uppercase sm:text-2xl">
                                Set Your Password
                            </h1>
                        </CardTitle>
                        <p className="m-0 mt-2 hidden md:inline">
                            Choose a new password to finish setting up your account.
                        </p>
                    </CardHeader>
                    {token ? <ResetPasswordForm token={token} /> : <InvalidLink />}
                </Card>
                <div className={styles.welcome}>
                    <IapLogo aria-hidden className="mt-2 size-10" />
                    <h2 className="m-0 text-sm font-bold tracking-wider text-sky-500 uppercase">Account Setup</h2>
                    <h3 className="m-0 text-2xl/tight font-bold tracking-wide text-pretty uppercase sm:text-3xl/tight">
                        Welcome to In-App Protection Console
                    </h3>
                    <p className="m-0 text-pretty opacity-70">
                        Set your new password to complete your account setup and secure your access.
                    </p>
                    <p className="m-0 hidden items-center gap-2 text-sm text-pretty opacity-70 md:flex">
                        <LockIcon aria-hidden className="xmt-1 size-4 shrink-0" />
                        This link is single-use and expires in 15 minutes.
                    </p>
                </div>
            </div>
        </div>
    )
}

/**
 * Submits a new password for the reset token.
 *
 * Renders the card body and footer, with the submit button in the footer.
 */
const ResetPasswordForm = ({ token }: { token: string }) => {
    const form = useForm({ schema: ResetPasswordSchema, validate: "blur" })
    const navigate = useNavigate()

    const sessionVM = useViewModel(SessionViewModel)
    const loggerVM = useViewModel(LoggerViewModel)

    const save = async ({ newPassword }: v.InferOutput<typeof ResetPasswordSchema>) => {
        const res = await sessionVM.resetPassword(token, newPassword)
        if (res.success) {
            loggerVM.logOk("Password reset successfully.")
            // Replace the history entry because the token has been spent; going
            // back should not reopen a form that can no longer succeed.
            navigate(AppRoutes.Login, { replace: true })
        } else {
            loggerVM.logErrorMessage("Failed to reset password.")
        }
    }

    return (
        // `contents` lets the card lay out the body and footer as its own children.
        <Form of={form} onSubmit={save} className="contents">
            <CardContent>
                <FieldGroup className="gap-4">
                    <PasswordField form={form} path="newPassword" label="New Password" autoComplete="new-password" />
                    <PasswordField
                        form={form}
                        path="confirmPassword"
                        label="Confirm Password"
                        autoComplete="new-password"
                    />
                </FieldGroup>
            </CardContent>
            <CardFooter className="mt-auto">
                <BusyButton
                    type="submit"
                    size="lg"
                    busy={form.isSubmitting}
                    disabled={!v.is(ResetPasswordSchema, getInput(form))}
                    className="h-12 w-full text-base font-bold tracking-wider uppercase"
                >
                    Save
                </BusyButton>
            </CardFooter>
        </Form>
    )
}

/**
 * Shown when the reset link has no token, such as when it was truncated or
 * entered manually.
 *
 * Renders the card body only; there is no link to offer in a footer.
 *
 * The server defaults to a 15-minute token lifetime, configurable with
 * `INAPPPROTECTION_PASSWORD_RESET_TOKENEXPIRATIONDURATION`.
 */
const InvalidLink = () => (
    <CardContent>
        <Alert variant="destructive" className="border-destructive/10 bg-destructive/5">
            <CircleAlertIcon />
            <AlertTitle className="text-base">This password reset link is no longer valid</AlertTitle>
            <AlertDescription className="text-sm text-balance">
                Password reset links expire after 15 minutes. Ask your administrator to send a new one.
            </AlertDescription>
        </Alert>
    </CardContent>
)

interface PasswordFieldProps {
    /**
     * Form state owned by the form above.
     */
    form: FormStore<typeof ResetPasswordSchema>
    /**
     * Schema field this input edits; also used to derive its id.
     */
    path: keyof v.InferInput<typeof ResetPasswordSchema>
    /**
     * Label displayed above the input.
     */
    label: string
    /**
     * Tells the password manager whether to fill a saved password or offer a new one.
     */
    autoComplete: "current-password" | "new-password"
}

/**
 * Renders a labelled password input with its validation errors.
 *
 * Every error is listed, so the user sees each password rule still broken.
 */
const PasswordField = ({ form, path, label, autoComplete }: PasswordFieldProps) => {
    const id = `password-reset-${path}`

    return (
        <FormField of={form} path={[path]}>
            {(field) => {
                const invalid = isFieldInvalid(field)

                return (
                    <Field data-invalid={invalid}>
                        <FieldLabel htmlFor={id}>{label}</FieldLabel>
                        <PasswordInput
                            {...field.props}
                            id={id}
                            value={field.input}
                            aria-invalid={invalid}
                            autoComplete={autoComplete}
                            className="h-12"
                        />
                        {invalid && <FieldError errors={field.errors?.map((message) => ({ message }))} />}
                    </Field>
                )
            }}
        </FormField>
    )
}
